/**
 * `ServiceWorkerRegistration.pushManager` 기반 `PushAdapter`.
 *
 * 등록(`navigator.serviceWorker.register`)은 **여기서 하지 않는다.** 스크립트 경로·스코프·
 * 갱신 시점은 앱의 사정이고(`packages/bloom-app` 의 `public/sw.js`), SDK 가 정해 버리면
 * 앱이 자기 Service Worker 를 둘로 나누게 된다. 이미 준비된 등록을 받아 쓴다.
 */
export class WebPushAdapter {
    registration;
    /**
     * `navigator.serviceWorker.ready` 를 그대로 넘기면 된다. 함수로 받는 이유는 **초기화
     * 시점에 아직 준비되지 않았을 수 있어서다** — 미리 await 하면 SDK 초기화 전체가 Service
     * Worker 활성화를 기다린다.
     */
    constructor(registration) {
        this.registration = registration;
    }
    /**
     * Push 구독이 가능한 환경인가.
     *
     * 세 가지를 전부 본다. iOS 는 홈 화면에 추가(A2HS)하기 전에는 `PushManager` 자체가
     * 없으므로(WB-08) 이 검사가 그 상황도 함께 덮는다 — 별도 UA 판정을 두지 않는 이유다.
     * UA 로 기기를 알아맞히는 코드는 기기가 바뀔 때마다 틀리고, 기능 검사는 틀리지 않는다.
     */
    isSupported() {
        return (typeof navigator !== 'undefined' &&
            'serviceWorker' in navigator &&
            typeof globalThis.PushManager !== 'undefined' &&
            typeof globalThis.Notification !== 'undefined');
    }
    /** 요청하지 않는다 (WB-07). 읽기만 한다. */
    getPermission() {
        if (!this.isSupported())
            return 'unsupported';
        return toPushPermission(Notification.permission);
    }
    /**
     * 권한을 요청한다. **사용자 제스처 직후에 화면이 부른다** (WB-07) —
     * 브라우저는 제스처 밖의 요청을 거부하거나 조용히 무시하고, 한 번 거부된 권한은
     * 사이트 설정에서만 되돌릴 수 있어 **두 번째 기회가 없다.**
     */
    async requestPermission() {
        if (!this.isSupported())
            return 'unsupported';
        return toPushPermission(await Notification.requestPermission());
    }
    /** 이미 있으면 그것을 쓴다. 매번 새로 만들면 서버에 죽은 구독이 쌓인다. */
    async subscribe(vapidPublicKey) {
        const registration = await this.registration();
        const existing = await registration.pushManager.getSubscription();
        if (existing)
            return toWebPushSubscription(existing);
        const created = await registration.pushManager.subscribe({
            // `false` 로 두면 최신 브라우저가 구독 자체를 거부한다. 우리 알림은 시청자가
            // TV 앞에서 기다리는 것이라 사용자에게 보이는 것이 맞다.
            userVisibleOnly: true,
            applicationServerKey: decodeVapidKey(vapidPublicKey),
        });
        return toWebPushSubscription(created);
    }
    async getSubscription() {
        if (!this.isSupported())
            return null;
        const registration = await this.registration();
        const existing = await registration.pushManager.getSubscription();
        return existing ? toWebPushSubscription(existing) : null;
    }
    async unsubscribe() {
        if (!this.isSupported())
            return;
        const registration = await this.registration();
        const existing = await registration.pushManager.getSubscription();
        if (existing)
            await existing.unsubscribe();
    }
}
/**
 * iOS 이면서 홈 화면에 추가되지 않은 상태인가 (WB-08).
 *
 * `isSupported()` 가 이미 `false` 를 주지만 **이유를 구분하지 못한다** — 화면이 "알림을
 * 켜 주세요" 를 띄우면 사용자가 켤 수 있는 스위치가 없다. 안내해야 하는 것은 홈 화면에
 * 추가하라는 것이고, 그 판정에는 기능 검사가 아니라 기기 판정이 필요하다.
 *
 * SDK 코어가 아니라 웹 어댑터 계층에 두는 이유가 이것이다 — UA 판정은 플랫폼 사정이다.
 */
export function needsIosInstallForPush() {
    if (typeof navigator === 'undefined')
        return false;
    const ua = navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (!isIos)
        return false;
    const standalone = navigator.standalone === true;
    return !standalone;
}
function toPushPermission(value) {
    return value === 'granted' ? 'granted' : value === 'denied' ? 'denied' : 'default';
}
/**
 * 브라우저의 `PushSubscription` 을 계약 형태로 옮긴다.
 *
 * `toJSON()` 의 `keys` 는 선택 필드라 타입상 빠질 수 있는데, **없으면 발송 자체가
 * 성립하지 않는다** — `web-push` 의 aes128gcm 암호화가 유효한 P-256 공개키와 16바이트
 * auth 를 요구한다(T4). 그러니 조용히 빈 값을 보내지 않고 여기서 던진다.
 */
function toWebPushSubscription(subscription) {
    const json = subscription.toJSON();
    const p256dh = json.keys?.['p256dh'];
    const auth = json.keys?.['auth'];
    if (!json.endpoint || !p256dh || !auth) {
        throw new Error('Push 구독에 endpoint 또는 키가 없다 — 이 구독으로는 발송할 수 없다');
    }
    const result = { endpoint: json.endpoint, keys: { p256dh, auth } };
    if (json.expirationTime !== undefined)
        result.expirationTime = json.expirationTime;
    return result;
}
/**
 * VAPID 공개키(base64url)를 `applicationServerKey` 가 받는 바이트열로 바꾼다.
 *
 * 문자열을 그대로 넘기는 것도 규격에는 있으나 **구현 편차가 있어** 바이트열로 준다.
 * 어긋나면 `subscribe()` 가 `InvalidCharacterError` 로 던지고, 그 증상은 "알림 권한은
 * 허용됐는데 구독만 안 된다" 라 원인이 화면에 드러나지 않는다.
 */
function decodeVapidKey(base64url) {
    const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
    const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    // `new Uint8Array(length)` 의 타입은 `Uint8Array<ArrayBufferLike>` 라 `BufferSource` 에
    // 들어가지 않는다(공유 버퍼일 수 있다고 본다). 버퍼를 먼저 만들어 좁힌다.
    const bytes = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i += 1)
        bytes[i] = raw.charCodeAt(i);
    return bytes;
}
//# sourceMappingURL=webpush-adapter.js.map